import "whatwg-fetch";
import { has, includes } from "lodash";
import { getVisaHost } from "@linkfluence/konf";
import { cleanTokens, redirectToLogout } from "@linkfluence/visa/auth";
import {
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
} from "@linkfluence/visa/tokens";
import { Evt } from "@tootools/event";

const HTTP_UNAUTHORIZED_STATUS = 401;

const EXPIRED_TOKEN_ERROR = "ExpiredTokenException";

const MISSING_TOKEN_ERROR = "MissingTokenException";

const INVALID_TOKEN_ERROR = "InvalidTokenException";

const USER_NOT_EXIST_ERROR = "UserNotExist";

function getHeadersFromRequestParams(params: RequestParams) {
  const headerContentType = { "Content-Type": "application/json" };
  const headerAuthorization = { Authorization: "Bearer " + getAuthToken() };

  if (params.isAsset) {
    return { ...headerAuthorization };
  }
  if (!hasAuthorizationFromRequestParams(params)) {
    return { ...headerContentType };
  }
  return {
    ...headerContentType,
    ...headerAuthorization,
  };
}

function hasAuthorizationFromRequestParams(params: RequestParams) {
  return (
    !params || !has(params, "withAuthorization") || params.withAuthorization
  );
}

type RequestParams = Pick<RequestInit, "method" | "body"> & {
  withAuthorization?: boolean;
  isAsset?: boolean;
};

interface APIOptions {
  endpoint: string;
}

export class LinkfluenceAPI {
  private options: APIOptions = {
    endpoint: "",
  };
  public constructor(options: APIOptions) {
    this.options = options;
  }
  // Prevent multiple requests in parallel triggering a token refresh
  private refreshTokenPending: boolean = false;

  private async fetchResponseAPI(
    pathname: string,
    params: RequestParams,
    withError = false
  ): Promise<Response> {
    const response = await fetch(this.options.endpoint + pathname, {
      ...params,
      headers: getHeadersFromRequestParams(params),
    });
    if (!response.ok) {
      try {
        // n.b. `response.body` is "used"/unaccessible after this line
        // use `response.clone().json()` if response needs to be returned in this block
        const responseBody = await response.json();
        const errorType = responseBody["error_type"] || response.statusText;
        if (response.status === HTTP_UNAUTHORIZED_STATUS) {
          if (errorType === EXPIRED_TOKEN_ERROR) {
            await this.refreshToken();
            return await this.fetchResponseAPI(pathname, params);
          } else if (
            includes([MISSING_TOKEN_ERROR, INVALID_TOKEN_ERROR], errorType)
          ) {
            cleanTokens();
            window.location.reload();
          }
          throw new Error(errorType);
        } else if (errorType === USER_NOT_EXIST_ERROR) {
          redirectToLogout();
        } else if (withError) {
          throw responseBody;
        } else {
          throw new Error(errorType);
        }
      } catch (e) {
        if (withError) {
          throw e;
        }
        throw new Error(response.statusText);
      }
    }
    return response;
  }
  public async postPutOrDeleteAPI<T>(
    pathname: string,
    body: object | File,
    method: "POST" | "DELETE" | "PUT",
    params: RequestParams = {},
    withError?: boolean
  ): Promise<T> {
    const _body = params.isAsset ? (body as File) : JSON.stringify(body);
    const response = await this.fetchResponseAPI(
      pathname,
      {
        method,
        body: _body,
        ...params,
      },
      withError
    );
    try {
      const json = await response.json();
      return json;
    } catch (_) {
      return null;
    }
  }
  public async post<T>(pathname: string, body?: object, withError?: boolean) {
    return await this.postPutOrDeleteAPI<T>(
      pathname,
      body,
      "POST",
      {},
      withError
    );
  }
  public async delete<T>(pathname: string, body?: object) {
    return await this.postPutOrDeleteAPI<T>(pathname, body, "DELETE");
  }
  public async put<T>(pathname: string, body?: object) {
    return await this.postPutOrDeleteAPI<T>(pathname, body, "PUT");
  }
  public async upload<T>(pathname: string, body: object) {
    return await this.postPutOrDeleteAPI<T>(pathname, body, "POST", {
      isAsset: true,
    });
  }
  public async get<T>(
    pathname: string,
    params?: Pick<RequestParams, "withAuthorization">,
    withError?: boolean
  ): Promise<T> {
    const response = await this.fetchResponseAPI(pathname, {
      method: "GET",
      ...params,
    });
    try {
      const json = await response.json();
      return json;
    } catch (e) {
      if (withError) {
        throw e;
      } else {
        return null;
      }
    }
  }
  async stream<T>(
    eventTarget: Evt,
    endpoint: string,
    body: object
  ): Promise<T> {
    try {
      const response = await this.fetchResponseAPI(endpoint, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const utf8Decoder = new TextDecoder("utf-8");
      const chunks = [];
      const reader = response.body.getReader();
      let { value, done: readerDone } = await reader.read();
      let chunk = value ? utf8Decoder.decode(value) : "";

      const re = /\r\n|\n|\r/gm;
      let startIndex = 0;

      for (;;) {
        const result = re.exec(chunk);
        if (!result) {
          if (readerDone) {
            break;
          }
          const remainder = chunk.substr(startIndex);
          ({ value, done: readerDone } = await reader.read());
          chunk = remainder + (value ? utf8Decoder.decode(value) : "");
          startIndex = re.lastIndex = 0;
          continue;
        }
        chunks.push(
          this.parseChunkAndDispatch(
            eventTarget,
            chunk.substring(startIndex, result.index)
          )
        );
        startIndex = re.lastIndex;
      }
      if (startIndex < chunk.length) {
        // last line didn't end in a newline char
        chunks.push(
          this.parseChunkAndDispatch(eventTarget, chunk.substr(startIndex))
        );
      }
    } catch (e) {
      console.error(e);
      eventTarget.emit(new CustomEvent("/error", { detail: e.message }));
    }
    return null;
  }
  private async refreshToken() {
    if (this.refreshTokenPending) {
      return;
    }
    this.refreshTokenPending = true;
    const response = await fetch(getVisaHost() + "/oauth2/token", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: getRefreshToken(),
      }),
    });
    if (!response.ok) {
      cleanTokens();
      window.location.reload();
      throw new Error(response.statusText);
    } else {
      const { access_token, refresh_token } = await response.json();
      setAuthToken(access_token);
      setRefreshToken(refresh_token);
      this.refreshTokenPending = false;
    }
  }
  private parseChunkAndDispatch(eventTarget: Evt, chunck: string) {
    try {
      const data = JSON.parse(chunck);
      eventTarget.emit(
        new MessageEvent("/data", {
          data,
        })
      );
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }
}
